import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { getImageUrl } from './utils/mediaUtils';
import { getAPIBase } from './config';
import { ArrowLeft, Calendar, User, Tag, CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react';

const STATIC_NEWS_DATA = {
  'community-involvement': {
    title: 'Community Involvement',
    subtitle: 'Outreach Programs & Service Learning',
    description: 'Highlights of our comprehensive outreach programs, service learning initiatives, and local collaborations led by students and staff.',
    category: 'Press Release',
    color: '#7c3aed',
    colorLight: '#ede9fe',
    icon: 'fa-handshake',
    images: [],
    date: 'October 2026',
    author: 'Communications Team',
    features: [
      {
        icon: 'fa-users',
        title: 'Student Leadership',
        detail: 'Students lead community service projects and mentor younger peers in outreach initiatives.'
      },
      {
        icon: 'fa-heart',
        title: 'Local Partnerships',
        detail: 'Collaborations with local organizations to address community needs and create meaningful impact.'
      },
      {
        icon: 'fa-book',
        title: 'Service Learning',
        detail: 'Integrated service projects that connect classroom learning with real-world community challenges.'
      },
      {
        icon: 'fa-globe',
        title: 'Global Awareness',
        detail: 'Programs that foster cultural understanding and global citizenship among our students.'
      }
    ],
    highlights: [
      'Over 500 students participated in community service this year',
      'Partnerships with 15+ local organizations and NGOs',
      'Monthly community outreach events and initiatives',
      'Student-led volunteer coordination and project management'
    ],
    content: 'Our community involvement programs reflect our commitment to developing socially responsible global citizens. Through structured service learning, students engage with real community challenges while developing leadership skills, empathy, and a sense of civic responsibility. Our partnerships with local organizations ensure that student efforts create meaningful, sustainable impact. From environmental conservation to educational support for underprivileged children, our students are making a difference in the community while learning valuable lessons about collaboration, problem-solving, and social responsibility.'
  },
  'partnerships': {
    title: 'Strategic Partnerships',
    subtitle: 'Collaborations That Strengthen Learning',
    description: 'Updates on strategic partnerships that enhance our educational programs, facilities, and student opportunities.',
    category: 'Press Release',
    color: '#06b6d4',
    colorLight: '#cffafe',
    icon: 'fa-link',
    images: [],
    date: 'September 2026',
    author: 'Administration',
    features: [
      {
        icon: 'fa-university',
        title: 'University Partnerships',
        detail: 'Collaborations with leading universities for advanced learning programs and research opportunities.'
      },
      {
        icon: 'fa-building',
        title: 'Corporate Partnerships',
        detail: 'Industry partnerships providing internships, mentorship, and career development opportunities.'
      },
      {
        icon: 'fa-microscope',
        title: 'Research Initiatives',
        detail: 'Joint research projects with academic institutions advancing educational innovation.'
      },
      {
        icon: 'fa-graduation-cap',
        title: 'Educational Programs',
        detail: 'Specialized programs developed in partnership with educational experts and organizations.'
      }
    ],
    highlights: [
      'Partnership with 8 leading universities for advanced programs',
      'Internship opportunities with 20+ corporate partners',
      'Joint research projects in STEM and innovation',
      'Collaborative curriculum development with educational experts'
    ],
    content: 'Strategic partnerships are central to our mission of providing world-class education. Through collaborations with universities, corporations, and educational organizations, we expand learning opportunities, provide real-world experience, and stay at the forefront of educational innovation. Our partnerships enable students to access advanced programs, internship opportunities, and mentorship from industry professionals. These collaborations also strengthen our facilities, curriculum, and overall educational ecosystem, ensuring our students are prepared for success in a rapidly changing world.'
  },
  'education-trends': {
    title: 'Education Trends',
    subtitle: 'Emerging Learning Approaches & Innovation',
    description: 'Insights on emerging learning approaches, curriculum updates, and global education trends shaping the future of learning.',
    category: 'Education Trends',
    color: '#f59e0b',
    colorLight: '#fef3c7',
    icon: 'fa-lightbulb',
    images: [],
    date: 'October 2026',
    author: 'Curriculum Team',
    features: [
      {
        icon: 'fa-brain',
        title: 'Personalized Learning',
        detail: 'Adaptive learning pathways tailored to individual student needs and learning styles.'
      },
      {
        icon: 'fa-network-wired',
        title: 'Digital Integration',
        detail: 'Seamless integration of technology to enhance engagement and accessibility in learning.'
      },
      {
        icon: 'fa-users-gear',
        title: 'Collaborative Learning',
        detail: 'Project-based and peer-learning approaches that develop teamwork and communication skills.'
      },
      {
        icon: 'fa-chart-line',
        title: 'Data-Driven Insights',
        detail: 'Using analytics to understand learning patterns and optimize educational outcomes.'
      }
    ],
    highlights: [
      'Implementation of interactive personalized learning systems',
      'Integration of global learning networks and virtual classrooms',
      'Focus on 21st-century skills: creativity, critical thinking, collaboration',
      'Emphasis on social-emotional learning and student wellbeing'
    ],
    content: 'Education is evolving rapidly, and we are committed to staying at the forefront of these changes. Modern education trends emphasize personalized learning, digital integration, and the development of critical thinking and collaboration skills. We continuously update our curriculum and teaching methodologies to reflect these trends, ensuring our students are prepared for a dynamic future. By embracing innovation while maintaining our core values, we create learning experiences that are both engaging and meaningful, preparing students not just for exams, but for life.'
  },
  'student-highlights': {
    title: 'Student Highlights',
    subtitle: 'Achievements, Creativity & Leadership',
    description: 'Stories of student achievements, creativity, leadership, and inspiring moments from our school community.',
    category: 'Student Highlights',
    color: '#ec4899',
    colorLight: '#fce7f3',
    icon: 'fa-star',
    images: [],
    date: 'September 2026',
    author: 'Student Affairs',
    features: [
      {
        icon: 'fa-trophy',
        title: 'Academic Excellence',
        detail: 'Outstanding achievements in academics, competitions, and scholarly pursuits.'
      },
      {
        icon: 'fa-palette',
        title: 'Creative Expression',
        detail: 'Artistic and creative projects showcasing student talent and innovation.'
      },
      {
        icon: 'fa-handshake',
        title: 'Leadership Impact',
        detail: 'Student leaders making positive changes in school and community.'
      },
      {
        icon: 'fa-heart',
        title: 'Character Development',
        detail: 'Stories of personal growth, resilience, and positive values in action.'
      }
    ],
    highlights: [
      '50+ students recognized for academic excellence this semester',
      'Student-led initiatives impacting 200+ community members',
      'Award-winning performances in arts and athletics',
      'Peer mentorship programs supporting 100+ younger students'
    ],
    content: 'Our students are the heart of our school community. Every day, they demonstrate remarkable achievements, creativity, and leadership. From academic excellence to artistic expression, from community service to personal growth, our students inspire us with their dedication and passion. These stories celebrate not just their accomplishments, but the character, resilience, and values they embody. We are proud to nurture young people who will become thoughtful, compassionate, and innovative leaders in their communities and beyond.'
  },
  'teacher-insights': {
    title: 'Teacher Insights',
    subtitle: 'Perspectives on Teaching & Learning',
    description: 'Perspective pieces from our educators about classroom experiences, teaching innovations, and learning journeys.',
    category: 'Teacher Insights',
    color: '#10b981',
    colorLight: '#d1fae5',
    icon: 'fa-chalkboard-user',
    images: [],
    date: 'August 2026',
    author: 'Faculty',
    features: [
      {
        icon: 'fa-book-open',
        title: 'Innovative Teaching',
        detail: 'Exploring new pedagogies and methods that enhance student engagement and learning.'
      },
      {
        icon: 'fa-comments',
        title: 'Student Connection',
        detail: 'Building meaningful relationships that support student growth and development.'
      },
      {
        icon: 'fa-flask',
        title: 'Continuous Learning',
        detail: 'Teachers as lifelong learners, staying current with educational research and practices.'
      },
      {
        icon: 'fa-lightbulb',
        title: 'Inspiring Curiosity',
        detail: 'Creating classroom environments where questions are encouraged and learning is joyful.'
      }
    ],
    highlights: [
      'Teachers completing 100+ hours of professional development annually',
      'Implementation of 15+ innovative teaching projects this year',
      'Student satisfaction ratings averaging 4.8/5 for teacher effectiveness',
      'Collaborative curriculum development across all departments'
    ],
    content: 'Our teachers are dedicated professionals who go beyond delivering curriculum—they inspire, mentor, and transform lives. Through their insights and experiences, we gain valuable perspectives on what makes learning meaningful and impactful. Our educators continuously innovate, collaborate, and grow, creating classroom environments where students feel supported, challenged, and inspired. Their commitment to excellence and their genuine care for student development are the foundation of our school\'s success.'
  }
};

function NewsDetailPage({ newsId, onNavigate, page }) {
  const [activeImageSlide, setActiveImageSlide] = useState(0);
  const [dynamicSectionData, setDynamicSectionData] = useState(null);
  const [mediaMap, setMediaMap] = useState(() => {
    try {
      const cached = sessionStorage.getItem('cms_media_map');
      if (cached) return JSON.parse(cached);
    } catch (e) {}
    return {};
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [newsId]);

  // Fetch dynamic sections and media map for News & Updates
  useEffect(() => {
    let isMounted = true;
    const fetchNewsData = async () => {
      const apiBase = getAPIBase();
      try {
        const [secRes, medRes] = await Promise.all([
          fetch(`${apiBase}/homepage/cms/sections?published=true&page=news-updates&_t=${Date.now()}`, {
            credentials: 'omit'
          }),
          fetch(`${apiBase}/homepage/cms/media?type=image&_t=${Date.now()}`, {
            credentials: 'omit'
          })
        ]);

        if (!isMounted) return;

        if (medRes.ok) {
          const medJson = await medRes.json();
          if (medJson.ok && Array.isArray(medJson.data)) {
            const map = {};
            const origin = 'https://permataharapansecondary.sch.id';
            medJson.data.forEach(m => {
              const mId = m.id || m.media_id;
              let mUrl = m.url || m.file_path;
              if (mUrl && !mUrl.startsWith('http://') && !mUrl.startsWith('https://')) {
                mUrl = `${origin}${mUrl.startsWith('/') ? '' : '/'}${mUrl}`;
              }
              if (mId && mUrl) {
                map[mId] = mUrl;
                map[String(mId)] = mUrl;
                const num = Number(mId);
                if (!isNaN(num)) map[num] = mUrl;
              }
            });
            setMediaMap(map);
            try {
              sessionStorage.setItem('cms_media_map', JSON.stringify(map));
            } catch (e) {}
          }
        }

        if (secRes.ok) {
          const secJson = await secRes.json();
          const sections = Array.isArray(secJson.data) ? secJson.data : Object.values(secJson.data || {});
          if (isMounted) {
            setDynamicSectionData(sections);
          }
        }
      } catch (e) {
        console.warn('[NewsDetailPage] Fetch error, using fallback:', e);
      }
    };

    fetchNewsData();

    // Re-fetch on approval or CMS content updates
    const handleApprovalComplete = () => {
      fetchNewsData();
    };
    window.addEventListener('approvalCompleted', handleApprovalComplete);
    window.addEventListener('cmsContentUpdated', handleApprovalComplete);

    return () => {
      isMounted = false;
      window.removeEventListener('approvalCompleted', handleApprovalComplete);
      window.removeEventListener('cmsContentUpdated', handleApprovalComplete);
    };
  }, [newsId]);

  // Resolve media URL helper
  const resolveImage = useCallback((imgSrc) => {
    if (!imgSrc) return null;

    const str = String(imgSrc).trim();
    if (!str) return null;

    // Check in mediaMap directly
    if (mediaMap) {
      if (mediaMap[str]) return mediaMap[str];
      const num = Number(str);
      if (!isNaN(num) && mediaMap[num]) return mediaMap[num];
    }

    const resolved = getImageUrl(str, mediaMap);
    if (resolved) {
      if (resolved.startsWith('http://') || resolved.startsWith('https://') || resolved.startsWith('blob:') || resolved.startsWith('data:')) {
        return resolved;
      }
      return `https://permataharapansecondary.sch.id${resolved.startsWith('/') ? '' : '/'}${resolved}`;
    }

    if (str.startsWith('http://') || str.startsWith('https://') || str.startsWith('blob:') || str.startsWith('data:')) {
      return str;
    }

    if (str.startsWith('/')) {
      return `https://permataharapansecondary.sch.id${str}`;
    }

    // If it's a numeric ID (e.g. "327") that hasn't resolved in mediaMap yet, NEVER return raw number
    if (/^\d+$/.test(str)) {
      return null;
    }

    return str;
  }, [mediaMap]);

  // Find article dynamically across articles and press releases
  const dynamicArticle = useMemo(() => {
    const normalizedKey = String(newsId || '')
      .toLowerCase()
      .replace(/^news-blog-/, '')
      .replace(/^news-press-/, '')
      .replace(/^news\//, '')
      .trim();

    // 1. Check fresh dynamicSectionData from database API FIRST (highest priority)
    if (dynamicSectionData && Array.isArray(dynamicSectionData)) {
      for (const section of dynamicSectionData) {
        let content = section.content_published;
        if (!content || content === '{}' || content === '[]') {
          content = section.content;
        }
        if (typeof content === 'string') {
          try {
            content = JSON.parse(content);
          } catch (e) {
            content = {};
          }
        }
        const en = content?.en || content || {};
        const items = [
          ...(Array.isArray(en.items) ? en.items : []),
          ...(Array.isArray(en.press_releases) ? en.press_releases : []),
          ...(Array.isArray(en.blog_articles) ? en.blog_articles : [])
        ];

        for (const it of items) {
          if (!it) continue;
          const itId = String(it.id || '').toLowerCase();
          const itLink = String(it.link || '').toLowerCase().replace(/^(news-blog-|news-press-)/, '');
          const itSlug = String(it.title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

          if (itId === normalizedKey || itLink === normalizedKey || itSlug === normalizedKey) {
            return it;
          }
        }
      }
    }

    // 2. Check passed page prop (fallback if API has not finished loading)
    const pageArticles = page?.blog_articles || [];
    const pagePress = page?.press_releases || [];
    const allPageItems = [...pageArticles, ...pagePress];

    for (const item of allPageItems) {
      if (!item) continue;
      const itemId = String(item.id || '').toLowerCase();
      const itemLink = String(item.link || '').toLowerCase().replace(/^(news-blog-|news-press-)/, '');
      const itemSlug = String(item.title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

      if (itemId === normalizedKey || itemLink === normalizedKey || itemSlug === normalizedKey) {
        return item;
      }
    }

    // 3. Fallback to cached news-updates page in sessionStorage
    try {
      const cached = sessionStorage.getItem('cms_page_news-updates') || sessionStorage.getItem('cms_page_news-and-updates');
      if (cached) {
        const parsed = JSON.parse(cached);
        const cachedItems = [...(parsed.blog_articles || []), ...(parsed.press_releases || [])];
        for (const item of cachedItems) {
          if (!item) continue;
          const itemId = String(item.id || '').toLowerCase();
          const itemLink = String(item.link || '').toLowerCase().replace(/^(news-blog-|news-press-)/, '');
          const itemSlug = String(item.title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
          if (itemId === normalizedKey || itemLink === normalizedKey || itemSlug === normalizedKey) {
            return item;
          }
        }
      }
    } catch (e) {}

    return null;
  }, [newsId, page, dynamicSectionData]);

  // Merge dynamic item or fallback to static newsData
  const news = useMemo(() => {
    const normalizedKey = String(newsId || '')
      .toLowerCase()
      .replace(/^news-blog-/, '')
      .replace(/^news-press-/, '')
      .replace(/^news\//, '')
      .trim();

    if (dynamicArticle) {
      const cat = dynamicArticle.category || dynamicArticle.tag || 'School Update';
      let themeColor = '#2563eb';
      let themeLight = '#eff6ff';
      let themeIcon = 'fa-newspaper';

      if (cat.toLowerCase().includes('trend')) {
        themeColor = '#f59e0b';
        themeLight = '#fef3c7';
        themeIcon = 'fa-lightbulb';
      } else if (cat.toLowerCase().includes('highlight')) {
        themeColor = '#ec4899';
        themeLight = '#fce7f3';
        themeIcon = 'fa-star';
      } else if (cat.toLowerCase().includes('teacher') || cat.toLowerCase().includes('insight')) {
        themeColor = '#10b981';
        themeLight = '#d1fae5';
        themeIcon = 'fa-chalkboard-user';
      } else if (cat.toLowerCase().includes('press') || dynamicArticle.link?.includes('press') || String(newsId || '').includes('press')) {
        themeColor = '#7c3aed';
        themeLight = '#ede9fe';
        themeIcon = dynamicArticle.icon || 'fa-bullhorn';
      }

      // Header Images (support up to 3 slides carousel or single cover image)
      const dynamicImagesList = (() => {
        let rawImgs = dynamicArticle.images;
        if (typeof rawImgs === 'string') {
          try {
            const parsed = JSON.parse(rawImgs);
            if (Array.isArray(parsed)) rawImgs = parsed;
          } catch (e) {}
        }
        if (Array.isArray(rawImgs) && rawImgs.length > 0) {
          const list = rawImgs.map(img => resolveImage(img)).filter(Boolean);
          if (list.length > 0) return list;
        }
        if (dynamicArticle.image) {
          const single = resolveImage(dynamicArticle.image);
          if (single) return [single];
        }
        return [];
      })();

      // Features / Key Pillars (Optional structured points)
      let rawFeatures = dynamicArticle.features;
      if (typeof rawFeatures === 'string') {
        try {
          rawFeatures = JSON.parse(rawFeatures);
        } catch (e) {
          rawFeatures = [];
        }
      }
      const features = Array.isArray(rawFeatures)
        ? rawFeatures.filter(f => f && (f.title || f.detail))
        : [];

      // Milestones / Highlights (Bullet points)
      let rawHighlights = dynamicArticle.highlights;
      if (typeof rawHighlights === 'string') {
        try {
          const parsed = JSON.parse(rawHighlights);
          if (Array.isArray(parsed)) rawHighlights = parsed;
        } catch (e) {}
      }
      const highlights = Array.isArray(rawHighlights)
        ? rawHighlights.filter(Boolean)
        : (typeof rawHighlights === 'string' && rawHighlights ? [rawHighlights] : []);

      return {
        title: dynamicArticle.title || 'News Update',
        subtitle: dynamicArticle.subtitle || cat,
        category: cat,
        description: dynamicArticle.description || 'Detailed report and updates from Permata Harapan Secondary.',
        content: dynamicArticle.content || dynamicArticle.description || 'Full article story content is being updated by the communications team.',
        color: themeColor,
        colorLight: themeLight,
        icon: themeIcon || 'fa-newspaper',
        images: dynamicImagesList,
        date: dynamicArticle.date || 'October 2026',
        author: dynamicArticle.author || 'Editorial Team',
        features,
        highlights
      };
    }

    // Static fallback
    return STATIC_NEWS_DATA[normalizedKey] || STATIC_NEWS_DATA['community-involvement'];
  }, [dynamicArticle, newsId, resolveImage]);

  useEffect(() => {
    if (!news.images || news.images.length <= 1) return;
    const intervalId = setInterval(() => {
      setActiveImageSlide((current) => (current + 1) % news.images.length);
    }, 5000);

    return () => clearInterval(intervalId);
  }, [news.images]);

  const handleBack = () => {
    if (onNavigate) {
      onNavigate('news-and-updates');
    } else {
      window.location.hash = '#news-and-updates';
    }
  };

  return (
    <section className="news-detail-page">
      {/* Hero Section */}
      <section className="news-detail-hero" style={{ backgroundColor: news.color }}>
        <div className="news-detail-hero-pattern" />
        <div className="news-detail-hero-image">
          <div className="news-detail-hero-media" aria-hidden="true">
            {news.images.map((image, index) => (
              <div
                key={`${news.title}-${index}-${image}`}
                className={`news-detail-slide ${index === activeImageSlide ? 'active' : ''}`}
              >
                <img src={image} alt={`${news.title} slide ${index + 1}`} />
              </div>
            ))}
          </div>
          <div className="news-detail-hero-overlay" aria-hidden="true" />
        </div>

        {/* Previous / Next slide buttons */}
        {news.images.length > 1 && (
          <>
            <button
              type="button"
              className="news-detail-nav-btn news-detail-nav-prev"
              onClick={(e) => {
                e.stopPropagation();
                setActiveImageSlide((prev) => (prev - 1 + news.images.length) % news.images.length);
              }}
              aria-label="Previous slide"
              style={{
                position: 'absolute',
                top: '50%',
                left: '20px',
                transform: 'translateY(-50%)',
                zIndex: 4,
                background: 'rgba(0, 0, 0, 0.45)',
                backdropFilter: 'blur(4px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '50%',
                width: '44px',
                height: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              <ChevronLeft size={22} />
            </button>
            <button
              type="button"
              className="news-detail-nav-btn news-detail-nav-next"
              onClick={(e) => {
                e.stopPropagation();
                setActiveImageSlide((prev) => (prev + 1) % news.images.length);
              }}
              aria-label="Next slide"
              style={{
                position: 'absolute',
                top: '50%',
                right: '20px',
                transform: 'translateY(-50%)',
                zIndex: 4,
                background: 'rgba(0, 0, 0, 0.45)',
                backdropFilter: 'blur(4px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '50%',
                width: '44px',
                height: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              <ChevronRight size={22} />
            </button>
          </>
        )}

        <div className="news-detail-hero-content">
          <button
            className="news-detail-back-btn"
            onClick={handleBack}
            aria-label="Back to news"
            type="button"
          >
            <ArrowLeft size={16} /> Back to News
          </button>
          
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.2)', padding: '4px 12px', borderRadius: '20px', color: '#fff', fontSize: '13px', fontWeight: 600, marginBottom: '12px', backdropFilter: 'blur(4px)' }}>
            <Tag size={13} /> {news.category}
          </div>

          <h1 className="news-detail-hero-title">{news.title}</h1>
          <p className="news-detail-hero-subtitle">{news.subtitle}</p>
          <div className="news-detail-meta">
            <span className="news-detail-date">
              <Calendar size={14} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '4px' }} />
              {news.date}
            </span>
            <span className="news-detail-author">
              <User size={14} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '4px' }} />
              {news.author}
            </span>
          </div>
        </div>

        {news.images.length > 1 && (
          <div className="news-detail-hero-dots">
            {news.images.map((_, index) => (
              <button
                key={`dot-${index}`}
                type="button"
                className={`news-detail-dot ${index === activeImageSlide ? 'active' : ''}`}
                onClick={() => setActiveImageSlide(index)}
                aria-label={`Show slide ${index + 1}`}
              />
            ))}
            <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '12px', fontWeight: 600, marginLeft: '6px' }}>
              {activeImageSlide + 1} / {news.images.length}
            </span>
          </div>
        )}
      </section>

      {/* Main Content */}
      <section className="news-detail-main">
        {/* About / Summary Section */}
        <div className="news-detail-section">
          <div className="news-detail-section-header">
            <h2>Overview & Summary</h2>
            <div className="news-detail-section-line" style={{ backgroundColor: news.color }} />
          </div>
          <p className="news-detail-description" style={{ whiteSpace: 'pre-line' }}>{news.description}</p>
          
          {/* Full Article Content Block */}
          <div className="news-detail-highlight-box" style={{ backgroundColor: news.colorLight, borderLeftColor: news.color }}>
            <i className={`fas ${news.icon}`} style={{ color: news.color }} />
            <div style={{ flex: 1 }}>
              <h4 style={{ margin: '0 0 10px 0', fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>
                {news.category?.toLowerCase().includes('press') ? 'Official Release Statement' : 'Full Article Story'}
              </h4>
              <p style={{ margin: 0, fontSize: '15px', lineHeight: '1.8', color: '#334155', whiteSpace: 'pre-line' }}>
                {news.content}
              </p>
            </div>
          </div>
        </div>

        {/* Features / Key Pillars Grid (Optional) */}
        {news.features && news.features.length > 0 && (
          <div className="news-detail-section">
            <div className="news-detail-section-header">
              <h2>Key Pillars & Highlights</h2>
              <div className="news-detail-section-line" style={{ backgroundColor: news.color }} />
            </div>
            <div className="news-features-grid">
              {news.features.map((feature, index) => (
                <div key={`feature-${index}`} className="news-feature-card" style={{ borderTopColor: news.color }}>
                  <div 
                    className="news-feature-icon" 
                    style={{ 
                      '--feature-color': news.color,
                      '--feature-color-dark': news.color + 'cc'
                    }}
                  >
                    <i className={`fas ${feature.icon || 'fa-star'}`} />
                  </div>
                  <h3>{feature.title}</h3>
                  <p>{feature.detail}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Milestones / Key Highlights (Bullet Points) */}
        {news.highlights && news.highlights.length > 0 && (
          <div className="news-detail-section">
            <div className="news-detail-section-header">
              <h2>Milestones & Key Points</h2>
              <div className="news-detail-section-line" style={{ backgroundColor: news.color }} />
            </div>
            <div className="news-highlights-grid">
              {news.highlights.map((highlight, index) => (
                <div key={`highlight-${index}`} className="news-highlight-card" style={{ backgroundColor: news.colorLight, borderLeftColor: news.color }}>
                  <div className="news-highlight-icon" style={{ color: news.color }}>
                    <CheckCircle2 size={18} />
                  </div>
                  <p>{highlight}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* CTA Section */}
      <section 
        className="news-detail-cta" 
        style={{ 
          '--cta-color': news.color,
          '--cta-color-dark': news.color + 'cc'
        }}
      >
        <div className="news-detail-cta-content">
          <h2>Explore More School Happenings</h2>
          <p>Read about campus events, academic milestones, and student achievements.</p>
          <button className="news-detail-cta-btn" onClick={handleBack} type="button">
            View All News & Updates <i className="fas fa-arrow-right" />
          </button>
        </div>
      </section>
    </section>
  );
}

export default NewsDetailPage;
